import { ProductEmailField } from "@/components/product-email-field";
import { Form } from "@/components/form/form";
import { FormField } from "@/components/form/form-field/form-field";
import { PhoneFormField } from "@/components/form/form-field/phone/phone-form-field";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { IconButton } from "@/components/ui/icon-button";
import { useAuth } from "@/hooks/auth/auth";
import { normalizeAuthRedirect } from "@/lib/auth-redirect";
import { validatePasswordPolicy } from "@/lib/password-policy";
import { toast } from "sonner";
import { useAuthRegister } from "@/orval/auth";
import type { RegisterDTO } from "@/orval/model";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { UserPlus2, LogIn } from "lucide-react";
import { z } from "zod";

const defaultValues: RegisterDTO = {
  productEmailUpdates: false,
  email: "",
  firstName: "",
  lastName: "",
  phoneNumber: "",
  password: "",
  passwordConfirmation: "",
};

const signUpSearchSchema = z.object({
  redirect: z.string().optional(),
});

const SignUp = () => {
  const { mutateAsync: registerAsync } = useAuthRegister();
  const { setToken } = useAuth();
  const navigate = useNavigate();
  const { redirect } = Route.useSearch();

  const onSubmit = async (data: RegisterDTO) => {
    const normalizedRedirect = normalizeAuthRedirect(redirect, window.location.origin);
    const { token, isEmailConfirmed, emailResponseMessage } = await registerAsync({ data });

    if (isEmailConfirmed) {
      if (token) {
        setToken(token);
        window.location.href = normalizedRedirect;
      } else {
        toast.error("Unable to automatically log you in. Please log in manually.");
        navigate({ to: "/login", search: { redirect: normalizedRedirect, message: undefined } });
      }
    } else {
      const defaultMessage =
        "A confirmation email has been sent. Please check your inbox and confirm your email before logging in.";
      toast.success(emailResponseMessage || defaultMessage);
      navigate({ to: "/login", search: { redirect: normalizedRedirect, message: undefined } });
    }
  };

  return (
    <div className="flex items-center justify-center h-full flex-1">
      <Card className="max-w-full w-96">
        <CardHeader>
          <CardTitle>Sign up</CardTitle>
          <CardDescription>Let's get to know each other a little better</CardDescription>
        </CardHeader>
        <CardContent>
          <Form<RegisterDTO>
            onSubmit={onSubmit}
            useFormProps={{ defaultValues }}
            containerClassName="grid grid-cols-2 gap-y-0 gap-4"
          >
            <FormField
              label="First Name"
              name="firstName"
              inputProps={{ placeholder: "Your first name", autoFocus: true }}
              rules={{ required: true }}
            />

            <FormField
              label="Last Name"
              name="lastName"
              inputProps={{ placeholder: "Your last name" }}
              rules={{ required: true }}
            />

            <FormField
              label="Email"
              name="email"
              className="col-span-2"
              inputProps={{ placeholder: "Your email address" }}
              rules={{
                required: true,
                validate: (value) => {
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  return emailRegex.test(value) || "Please enter a valid email address";
                },
              }}
            />

            <PhoneFormField
              label="Phone Number"
              name="phoneNumber"
              className="col-span-2"
              maskedInputProps={{ placeholder: "Your phone number", type: "tel" }}
              rules={{
                required: true,
                validate: (value) => {
                  const phoneRegex = /^\+?[1-9]\d{0,14}([ -]?\d+)*$/;
                  return phoneRegex.test(value) || "Please enter a valid phone number";
                },
              }}
            />

            <FormField
              label="Password"
              name="password"
              className="col-span-2"
              inputProps={{ placeholder: "Your password", type: "password" }}
              rules={{ required: true, validate: validatePasswordPolicy }}
            />

            <FormField
              label="Password Confirmation"
              name="passwordConfirmation"
              className="col-span-2"
              inputProps={{ placeholder: "Confirm your password", type: "password" }}
              rules={{
                required: true,
                validate: (value, { password }) => value === password || "Passwords do not match",
              }}
            />

            <div className="col-span-2">
              <ProductEmailField name="productEmailUpdates" />
            </div>

            <IconButton type="submit" icon={<UserPlus2 />} className="w-full col-span-2">
              Sign up
            </IconButton>
          </Form>
        </CardContent>
        <CardFooter className="flex flex-col gap-4 justify-between mt-4">
          <small className="text-xs text-zinc-600 font-medium leading-none">Already have an account?</small>

          <IconButton
            className="w-full"
            variant="outline"
            icon={<LogIn />}
            onClick={() =>
              navigate({
                to: "/login",
                search: { redirect: normalizeAuthRedirect(redirect, window.location.origin), message: undefined },
              })
            }
          >
            Login
          </IconButton>
        </CardFooter>
      </Card>
    </div>
  );
};

export const Route = createFileRoute("/_marketing/sign-up")({
  validateSearch: signUpSearchSchema,
  component: SignUp,
});
